import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import DynamicForm from "../../../../data/Axios/DynamicForm";
import { AccountsandFinanceurl } from "../../../../config";
import {
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { PaymentMethodQuery } from "../../../../data/Axios/queries";

const base_url = AccountsandFinanceurl.uri;

const AccountsTransfersForm = (props) => {
  const storedToken = localStorage.getItem("decodedToken");
  const decodedToken = storedToken ? JSON.parse(storedToken) : null;

  const companyId = decodedToken?.CompanyId
    ? parseInt(decodedToken.CompanyId)
    : null;

  const userId = decodedToken?.UserId
    ? parseInt(decodedToken.UserId)
    : null;

  /* ============================
     STATE
  ============================ */

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [fromAccountOptions, setFromAccountOptions] = useState([]);
  const [toAccountOptions, setToAccountOptions] = useState([]);
  const [loadingFromAccounts, setLoadingFromAccounts] = useState(false);
  const [loadingToAccounts, setLoadingToAccounts] = useState(false);
  // dependent account options are provided by the DynamicForm via onFieldChange

  /* ============================
     INITIAL VALUES
  ============================ */

  const initialValues = {
    entryDate: props.data?.entryDate || "",
    description: props.data?.description || "",
    accountFromType: props.data?.accountFromType || "",
    accountFromId: props.data?.accountFromId || "",
    accountToType: props.data?.accountToType || "",
    accountToId: props.data?.accountToId || "",
    amount: props.data?.amount || "",
    referenceNumber: props.data?.referenceNumber || "",
    isActive: props.data?.isActive ?? true,
    id: props.data?.id || 0,
  };

  const [formValues, setFormValues] = useState(initialValues);

  // keep formValues in sync when editing data changes
  useEffect(() => {
    setFormValues(initialValues);
  }, [props.data]);

  /* ============================
     LOAD PAYMENT METHODS
  ============================ */

  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        setLoadingPaymentMethods(true);
        const res = await graphqlQuery(PaymentMethodQuery, base_url);
        setPaymentMethods(
          (res || []).map((m) => ({
            label: m.accountName,
            value: m.id,
          }))
        );
      } catch (err) {
        console.error("Failed to load payment methods", err);
      } finally {
        setLoadingPaymentMethods(false);
      }
    };

    loadPaymentMethods();
  }, []);

  // If we're editing or initial account types are present, preload dependent account options
  useEffect(() => {
    const preloadDependentOptions = async () => {
      try {
        if (initialValues.accountFromType) {
          setLoadingFromAccounts(true);
          const query = `{ accountsBySubCategoryId(functionparams: { accountSubcategoryId: ${initialValues.accountFromType} }) { id accountName } }`;
          const res = await graphqlQuery(query, base_url);
          setFromAccountOptions(
            (res || []).map((a) => ({ value: a.id, label: a.accountName }))
          );
        }

        if (initialValues.accountToType) {
          setLoadingToAccounts(true);
          const query = `{ accountsBySubCategoryId(functionparams: { accountSubcategoryId: ${initialValues.accountToType} }) { id accountName } }`;
          const res = await graphqlQuery(query, base_url);
          setToAccountOptions(
            (res || []).map((a) => ({ value: a.id, label: a.accountName }))
          );
        }
      } catch (err) {
        console.error("Failed to preload dependent accounts", err);
      } finally {
        setLoadingFromAccounts(false);
        setLoadingToAccounts(false);
      }
    };

    preloadDependentOptions();
    // only run on mount / when initialValues change
  }, [initialValues.accountFromType, initialValues.accountToType]);

  /* dependent account options are fetched in handleFieldChange */

  /* ============================
     SUBMIT
  ============================ */

  const handleSubmit = async (values, { setSubmitting }) => {
    if (
      values.accountFromType === values.accountToType &&
      values.accountFromId === values.accountToId
    ) {
      Swal.fire(
        "Invalid Transfer",
        "From and To accounts cannot be the same.",
        "warning"
      );
      setSubmitting(false);
      return;
    }

    try {
      const payload = props.isEditing
        ? {
            id: values.id,
            entryDate: values.entryDate,
            description: values.description,
            accountFromType: parseInt(values.accountFromType),
            accountFromId: parseInt(values.accountFromId),
            accountToType: parseInt(values.accountToType),
            accountToId: parseInt(values.accountToId),
            amount: parseFloat(values.amount),
            referenceNumber: values.referenceNumber,
            isActive: values.isActive,
            updatedBy: userId,
          }
        : {
            entryDate: values.entryDate,
            description: values.description,
            accountFromType: parseInt(values.accountFromType),
            accountFromId: parseInt(values.accountFromId),
            accountToType: parseInt(values.accountToType),
            accountToId: parseInt(values.accountToId),
            amount: parseFloat(values.amount),
            referenceNumber: values.referenceNumber,
            isActive: values.isActive,
            companyId,
            createdBy: userId,
            isApproved: 0,
          };

      const result = await generateAndExecuteMutation(
        props.isEditing
          ? "updateAccountTransfer"
          : "createAccountTransfer",
        payload,
        "accountTransfers",
        base_url
      );

      if (result && Object.values(result)[0] !== null) {
        props.onClose();
        props.onAction();
        Swal.fire(
          "Success",
          `Account transfer ${
            props.isEditing ? "updated" : "created"
          } successfully`,
          "success"
        );
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to save account transfer", "error");
    } finally {
      setSubmitting(false);
    }
  };

  /* ============================
     FORM FIELDS
  ============================ */

  const fields = [
    {
      name: "accountFromType",
      label: "From Account Type",
      type: "select",
      required: true,
      options: paymentMethods,
      loading: loadingPaymentMethods,
      gridColumn: "span 2",
      parent: "main",
    },
    {
      name: "accountFromId",
      label: "From Account Name",
      type: "select",
      required: true,
      options: fromAccountOptions,
      disabled: !formValues.accountFromType,
      loading: loadingFromAccounts,
      gridColumn: "span 2",
      parent: "dependent",
    },
    {
      name: "accountToType",
      label: "To Account Type",
      type: "select",
      required: true,
      options: paymentMethods,
      loading: loadingPaymentMethods,
      gridColumn: "span 2",
      parent: "main",
    },
    {
      name: "accountToId",
      label: "To Account Name",
      type: "select",
      required: true,
      options: toAccountOptions,
      disabled: !formValues.accountToType,
      loading: loadingToAccounts,
      gridColumn: "span 2",
      parent: "dependent",
    },
    {
      name: "amount",
      label: "Amount",
      type: "number",
      required: true,
      gridColumn: "span 2",
    },
    {
      name: "referenceNumber",
      label: "Reference Number",
      type: "text",
      gridColumn: "span 2",
    },
    {
      name: "description",
      label: "Description",
      type: "text",
      gridColumn: "span 2",
    },
    {
      name: "entryDate",
      label: "Entry Date",
      type: "date",
      required: true,
      gridColumn: "span 2",
    },
    {
      name: "isActive",
      label: "Active",
      type: "checkbox",
    },
  ];

  const handleFieldChange = async (field, value) => {
    // When a parent account type is selected, fetch and return dependent account options
    if (field.name === "accountFromType" && value) {
      try {
        const query = `{ accountsBySubCategoryId(functionparams: { accountSubcategoryId: ${value} }) { id accountName } }`;
        const res = await graphqlQuery(query, base_url);
        const options = res
          ? res.map((a) => ({ value: a.id, label: a.accountName }))
          : [];
        setFromAccountOptions(options);
        setFormValues((prev) => ({ ...prev, accountFromType: value }));
        return options;
      } catch (err) {
        console.error("Failed to fetch FROM accounts", err);
        return [];
      }
    }

    if (field.name === "accountToType" && value) {
      try {
        const query = `{ accountsBySubCategoryId(functionparams: { accountSubcategoryId: ${value} }) { id accountName } }`;
        const res = await graphqlQuery(query, base_url);
        const options = res
          ? res.map((a) => ({ value: a.id, label: a.accountName }))
          : [];
        setToAccountOptions(options);
        setFormValues((prev) => ({ ...prev, accountToType: value }));
        return options;
      } catch (err) {
        console.error("Failed to fetch TO accounts", err);
        return [];
      }
    }

    return null; // no dependent options
  };

  return (
    <DynamicForm
      initialValues={initialValues}
      fields={fields}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      title={
        props.isEditing
          ? "Edit Account Transfer"
          : "Create Account Transfer"
      }
      subtitle="Transfer funds between payment accounts"
    />
  );
};

export default AccountsTransfersForm;
